import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as supabaseAuth from '../services/auth/supabaseAuth';
import { getSupabaseClient } from '../services/auth/supabase';
import { AuthModal } from '../components/auth/AuthModal';
import { AuthContext, type AuthContextValue, type AuthModalMode, type AuthUser } from './AuthContext';
import { getUserProfileRepository } from '../repositories';
import type { UserProfile, SubscriptionStatus } from '../types/user';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { isAbortError } from '../lib/clientLogger';

// Type alias for compatibility - Supabase user has slightly different shape
type User = SupabaseUser;

type AnalyticsUserProperties = {
  subscription_tier: 'Free' | 'Pro' | 'Elite';
  subscription_status: 'active' | 'cancelled' | 'trialing' | 'none';
  signup_date: string;
  email?: string;
};

let sentryModulePromise: Promise<typeof import('../lib/sentry')> | null = null;
let posthogModulePromise: Promise<typeof import('../lib/posthog')> | null = null;

function loadSentryModule() {
  if (sentryModulePromise === null) {
    sentryModulePromise = import('../lib/sentry');
  }

  return sentryModulePromise;
}

function loadPostHogModule() {
  if (posthogModulePromise === null) {
    posthogModulePromise = import('../lib/posthog');
  }

  return posthogModulePromise;
}

/**
 * Map subscription status to PostHog tier format
 */
function mapSubscriptionToPostHogTier(status: SubscriptionStatus): 'Free' | 'Pro' | 'Elite' {
  switch (status) {
    case 'pro':
      return 'Pro';
    case 'elite':
      return 'Elite';
    case 'free':
    default:
      return 'Free';
  }
}

/**
 * Map subscription status to PostHog subscription status format
 */
function mapSubscriptionStatus(
  status: SubscriptionStatus,
  _profile: UserProfile
): 'active' | 'cancelled' | 'trialing' | 'none' {
  // Free tier has no subscription
  if (status === 'free') {
    return 'none';
  }
  // For paid tiers, assume active (could check stripeSubscriptionId for more detail)
  return 'active';
}

// E2E auth bypass — when enabled, skip Supabase entirely and inject synthetic user
const isE2EAuthBypass = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

function getE2EBypassState(): {
  user: AuthUser;
  profile: UserProfile;
} {
  const uid = import.meta.env.VITE_DEV_AUTH_BYPASS_UID || '00000000-0000-0000-0000-000000000001';
  const email = import.meta.env.VITE_DEV_AUTH_BYPASS_EMAIL || 'dev@stellartrail.local';
  const username = import.meta.env.VITE_DEV_AUTH_BYPASS_USERNAME || 'dev';
  const tier = (import.meta.env.VITE_DEV_AUTH_BYPASS_TIER || 'elite') as SubscriptionStatus;

  return {
    user: {
      uid,
      email,
      emailVerified: true,
      displayName: username,
      photoURL: null,
    },
    profile: {
      id: uid,
      email,
      username,
      createdAt: Date.now(),
      subscriptionStatus: tier,
    },
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const supabaseClient = useMemo(() => getSupabaseClient(), []);
  const auth = supabaseClient?.auth ?? null;
  const isAuthConfigured = Boolean(supabaseClient);

  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [isSupabaseAuthReady, setIsSupabaseAuthReady] = useState<boolean>(isE2EAuthBypass);
  const [supabaseProfile, setSupabaseProfile] = useState<UserProfile | null>(null);
  const [isSupabaseProfileReady, setIsSupabaseProfileReady] = useState<boolean>(isE2EAuthBypass);

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode>('login');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);

  // Create repository instance when db is available
  const profileRepo = useMemo(() => {
    return getUserProfileRepository();
  }, []);

  // Track whether user was previously signed in (for session expiry detection).
  // Using a ref so the onAuthStateChange callback always sees the latest value
  // without needing to re-subscribe when supabaseUser changes.
  const hadUserRef = useRef(false);
  useEffect(() => { hadUserRef.current = supabaseUser !== null; }, [supabaseUser]);

  // Effect 1: Auth state subscription — SYNCHRONOUS callback only.
  // The callback must NOT await any Supabase calls (e.g. profileRepo.getProfile)
  // because it runs inside the auth lock.  Calling auth.getSession() (which
  // every Supabase query does internally for the access token) from inside the
  // lock would deadlock our in-memory serializer.
  useEffect(() => {
    if (isE2EAuthBypass) return;

    if (!auth) {
      setSupabaseUser(null);
      setIsSupabaseAuthReady(true);
      setIsSupabaseProfileReady(true);
      return;
    }

    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      const nextUser = session?.user ?? null;
      setSupabaseUser(nextUser);
      setIsSupabaseAuthReady(true);

      // Session expiry detection
      if (event === 'SIGNED_OUT' && hadUserRef.current) {
        setSessionExpired(true);
        setAuthModalMode('login');
        setAuthModalOpen(true);
      } else if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        setSessionExpired(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [auth]);

  // Effect 2: Profile loading — runs OUTSIDE the auth lock, triggered by
  // supabaseUser changes.  Safe to call profileRepo (which internally calls
  // auth.getSession() for the access token).
  useEffect(() => {
    if (isE2EAuthBypass) return;

    if (!supabaseUser || !profileRepo) {
      setSupabaseProfile(null);
      setEmailVerificationRequired(false);
      setIsSupabaseProfileReady(true);
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const emailVerified = supabaseUser.email_confirmed_at !== null;
        if (!emailVerified) {
          setEmailVerificationRequired(true);
          setAuthModalOpen(true);
          setSupabaseProfile(null);
          setIsSupabaseProfileReady(true);
          return;
        }

        setEmailVerificationRequired(false);
        let profile = await profileRepo.getProfile(supabaseUser.id);

        if (!profile) {
          profile = profileRepo.createDefaultProfile(supabaseUser.id, supabaseUser.email || '');
          await profileRepo.insertProfileIfNotExists(profile);
          profile = await profileRepo.getProfile(supabaseUser.id) ?? profile;
        }

        if (!cancelled) {
          setSupabaseProfile(profile);
        }
      } catch (err) {
        if (!cancelled && !isAbortError(err)) {
          console.error('Failed to load profile:', err);
          setSupabaseProfile(null);
        }
      } finally {
        if (!cancelled) {
          setIsSupabaseProfileReady(true);
        }
      }
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [supabaseUser, profileRepo]);

  const openAuthModal = useCallback((mode?: AuthModalMode) => {
    if (mode) setAuthModalMode(mode);
    setAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalOpen(false);
  }, []);

  const signOut = useCallback(async () => {
    if (!auth) return;

    // Clear sensitive localStorage data before signing out
    localStorage.removeItem('st-coach-messages');
    localStorage.removeItem('st-coach-conversation-id');
    localStorage.removeItem('st-coach-open');
    localStorage.removeItem('st-custom-indicator-apply-id');

    await supabaseAuth.signOut();
  }, [auth]);

  // Convert Supabase user to AuthUser shape for compatibility
  const supabaseAuthUser: AuthUser | null = supabaseUser ? {
    uid: supabaseUser.id,
    email: supabaseUser.email ?? null,
    emailVerified: supabaseUser.email_confirmed_at !== null,
    displayName: supabaseUser.user_metadata?.display_name ?? supabaseUser.user_metadata?.full_name ?? null,
    photoURL: supabaseUser.user_metadata?.avatar_url ?? null,
  } : null;

  const e2eBypass = isE2EAuthBypass ? getE2EBypassState() : null;
  const user: AuthUser | null = e2eBypass?.user ?? supabaseAuthUser;
  const userProfile: UserProfile | null = e2eBypass?.profile ?? supabaseProfile;
  const isAuthReady = isSupabaseAuthReady;
  const isProfileReady = isSupabaseProfileReady;

  // Update Sentry user context when user or profile changes
  useEffect(() => {
    if (user && userProfile) {
      void loadSentryModule().then(({ setSentryUser }) => {
        setSentryUser({
          id: user.uid,
          email: user.email ?? undefined,
          tier: userProfile.subscriptionStatus,
        });
      });
    } else {
      void loadSentryModule().then(({ setSentryUser }) => {
        setSentryUser(null);
      });
    }
  }, [user, userProfile]);

  // Update PostHog user identification when user or profile changes
  // Use refs to prevent infinite loops from object identity changes
  const lastIdentifiedState = React.useRef<string | null>(null);

  useEffect(() => {
    if (user && userProfile) {
      // Create a stable key from actual values (not object references)
      const stateKey = `${user.uid}:${userProfile.subscriptionStatus}:${user.email || ''}`;

      // Only identify if state actually changed to prevent loops
      if (lastIdentifiedState.current === stateKey) return;
      lastIdentifiedState.current = stateKey;

      // Identify user in PostHog with initial properties
      const postHogProperties: AnalyticsUserProperties = {
        subscription_tier: mapSubscriptionToPostHogTier(userProfile.subscriptionStatus),
        subscription_status: mapSubscriptionStatus(userProfile.subscriptionStatus, userProfile),
        signup_date: new Date(userProfile.createdAt).toISOString(),
        // Don't include last_active_date - it changes every time and causes loops
      };

      // Add email if available
      if (user.email) {
        postHogProperties.email = user.email;
      }

      void loadPostHogModule().then(({ identifyUser }) => {
        identifyUser(user.uid, postHogProperties);
      });
      // Don't call setUserProperties separately - it's redundant and causes extra calls
    } else if (!user) {
      // User logged out - reset PostHog identity
      if (lastIdentifiedState.current !== null) {
        lastIdentifiedState.current = null;
        void loadPostHogModule().then(({ resetUser }) => {
          resetUser();
        });
      }
    }
  // Only depend on stable primitive values, not objects
  }, [user?.uid, user?.email, userProfile?.subscriptionStatus, userProfile?.createdAt]);

  const value: AuthContextValue = {
    isAuthConfigured,
    isAuthReady,
    user,
    userProfile,
    isProfileReady,
    emailVerificationRequired,
    authModalOpen,
    authModalMode,
    openAuthModal,
    closeAuthModal,
    setAuthModalMode,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthModal />
    </AuthContext.Provider>
  );
};
